<script setup lang="tsx">
  import CustomDropdown from '~/components/UI/Forms/CustomDropdown.vue';
  import CustomInput from '~/components/UI/Forms/CustomInput.vue';
  import CustomRadio from '~/components/UI/Forms/CustomRadio.vue';
  import RoleBasedButton from '~/components/UI/Others/RoleBasedButton.vue';
  import { get, post } from '~/services/donationScreen';
  import type { GetResponse, ResponseStatus, AttachmentCategoryDrawer, PostResponse } from '~/types';
  import EditPasswordModal from '../../Modals/EditPasswordModal.vue';
  import AuditLogs from '../RoutedDrawerComponents/AuditLogs.vue';
  import { attachmentCategoryModuleOptions } from '~/functions/attachmentCategoryModules';
  import { InformationCircleIcon } from '@heroicons/vue/solid';

  const props = defineProps<{
    onClose: () => void;
    identifier: string;
  }>();

  const route = useRoute();
  const action = computed(() => route.query.action as string);

  const formRef = ref();
  const formState = reactive({
    module: '',
    expenseType: '',
    categoryName: '',
    isMandatory: false
  });
  const status = ref<boolean>(false);
  const attachmentsCount = ref<number>(0);
  const auditLogData = ref<any[]>([]);
  const isModalVisible = ref<boolean>(false);
  const isModalVisibleForDisable = ref<boolean>(false);
  const editStatus = ref<ResponseStatus>('idle');

  // Expense Type options come from the org's configured expense types - fully dynamic, org
  // admins name/rename these in Activate Features.
  const expenseTypeOptions = ref<{ title: string; value: string }[]>([]);

  const fetchExpenseTypeOptions = async () => {
    try {
      const response = await get<GetResponse<any>>('v1/get_org_info');
      const categories = response?.data?.data?.expense_categories_list || [];
      expenseTypeOptions.value = categories
        .filter((category: any) => category.value)
        .map((category: any) => ({ title: category.title, value: category.value }));
    } catch (error) {
      console.error('Error fetching expense type options:', error);
    }
  };

  const rules = {
    module: [{ required: true, message: 'Module is required', trigger: 'submit' }],
    categoryName: [{ required: true, message: 'Category Name is required', trigger: 'submit' }]
  };

  const mandatoryRadioOptions = [
    { label: 'Yes', value: true },
    { label: 'No', value: false }
  ];

  const updateStateField = (value: any, stateKey: keyof typeof formState) => {
    (formState as any)[stateKey] = value;
    if (stateKey === 'module' && value !== 'expenses') {
      formState.expenseType = '';
    }
  };

  const fetchData = async () => {
    try {
      const response = await get<GetResponse<AttachmentCategoryDrawer>>(
        `v1/get_attachment_category/${props.identifier}`
      );
      const category = response?.data?.data?.attachment_category;
      status.value = category?.is_disabled;
      attachmentsCount.value = response?.data?.data?.attachments_count || 0;
      auditLogData.value = response?.data?.data?.audit_log_data || [];
      Object.assign(formState, {
        module: category?.module || '',
        expenseType: category?.expense_type || '',
        categoryName: category?.category_name || '',
        isMandatory: category?.is_mandatory || false
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const submitHandler = async (modalData: { password: string }) => {
    try {
      editStatus.value = 'pending';
      const response = await post<PostResponse<AttachmentCategoryDrawer>>(
        `v1/attachment_category/${props.identifier}`,
        {
          data: {
            category_name: formState.categoryName,
            is_mandatory: formState.isMandatory,
            password: modalData.password
          }
        }
      );

      if (response.status === 401) {
        editStatus.value = 'error';
        return openFailedCustomNotification('Wrong Password!');
      }

      if (response.data.status === 'success') {
        openSuccessfulCustomNotification('Attachment Type Updated Successfully');
        isModalVisible.value = false;
        props.onClose();
      } else {
        openFailedCustomNotification(response.data.message);
        isModalVisible.value = false;
        editStatus.value = 'error';
      }
    } catch (error) {
      console.error(error);
      openFailedCustomNotification('Failed to Update Attachment Type');
      editStatus.value = 'error';
    }
  };

  const handleCreate = async () => {
    editStatus.value = 'pending';
    try {
      const response = await post<PostResponse<AttachmentCategoryDrawer>>('v1/add_attachment_category', {
        data: {
          module: formState.module,
          expense_type: formState.expenseType,
          category_name: formState.categoryName,
          is_mandatory: formState.isMandatory
        }
      });

      if (response.data.status === 'success') {
        openSuccessfulCustomNotification('Attachment Type Created Successfully');
        props.onClose();
        editStatus.value = 'success';
      } else {
        editStatus.value = 'error';
        return openFailedCustomNotification(response.data.message || 'Failed to Create Attachment Type');
      }
    } catch (error) {
      console.error(error);
      openFailedCustomNotification('Failed to Create Attachment Type');
      editStatus.value = 'error';
    }
  };

  const openDisableModal = () => {
    isModalVisibleForDisable.value = true;
  };

  const handleDisableAttachmentType = async (modalData: { password: string }) => {
    editStatus.value = 'pending';
    try {
      const response = await post<any>(`/v1/attachment_category_disabled/${props.identifier}`, {
        data: {
          is_disabled: true,
          password: modalData.password
        }
      });

      if (response.status === 401) {
        editStatus.value = 'error';
        return openFailedCustomNotification('Wrong Password!');
      }

      if (response.data.status === 'success') {
        openSuccessfulCustomNotification('Attachment Type Disabled Successfully');
        isModalVisibleForDisable.value = false;
        props.onClose();
        editStatus.value = 'success';
      } else {
        editStatus.value = 'error';
        openFailedCustomNotification(response.data.message || 'Failed to Disable Attachment Type');
      }
    } catch (error) {
      console.error(error);
      openFailedCustomNotification('Failed to Disable Attachment Type');
      editStatus.value = 'error';
    }
  };

  const handleActiveAttachmentType = async () => {
    editStatus.value = 'pending';
    try {
      const response = await post<any>(`/v1/attachment_category_enabled/${props.identifier}`, {
        data: { is_disabled: false }
      });

      if (response.data.status === 'success') {
        openSuccessfulCustomNotification('Attachment Type Activated Successfully');
        isModalVisibleForDisable.value = false;
        props.onClose();
        editStatus.value = 'success';
      } else {
        editStatus.value = 'error';
        openFailedCustomNotification(response.data.message || 'Failed to Activate Attachment Type');
      }
    } catch (error) {
      console.error(error);
      openFailedCustomNotification('Failed to Activate Attachment Type');
      editStatus.value = 'error';
    }
  };

  const handleCreateAttachmentType = async () => {
    try {
      await formRef.value.validate();

      if (route.query.action === 'edit') {
        return openModal();
      }

      await handleCreate();
    } catch (error) {
      console.error(error);
    }
  };

  const openModal = async () => {
    try {
      const isFormValid = await formRef.value.validate();
      if (!isFormValid) throw new Error('Form validation failed!');
      isModalVisible.value = true;
    } catch (error) {
      console.error(error);
    }
  };

  const handleModalCancel = () => {
    isModalVisible.value = false;
  };

  const handleModalDisableCancel = () => {
    isModalVisibleForDisable.value = false;
  };

  // The usage count is only known once the record is fetched, so the disable warning is
  // built after fetchData() resolves rather than as a static string.
  const disableWarningMessage = computed(() => {
    if (attachmentsCount.value > 0) {
      return `This category is already used by ${attachmentsCount.value} existing attachment(s). Disabling it will hide it from new uploads, but attachments already tagged with it are unaffected. Are you sure you want to Disable this Attachment Type?`;
    }
    return 'Are you sure you want to Disable this Attachment Type?';
  });

  onMounted(() => {
    fetchExpenseTypeOptions();
    if (props.identifier) {
      fetchData();
    }
  });

  watch(
    () => props.identifier,
    (newIdentifier, oldIdentifier) => {
      if (newIdentifier === oldIdentifier) return;
      if (newIdentifier) {
        fetchData();
      } else {
        Object.assign(formState, { module: '', expenseType: '', categoryName: '', isMandatory: false });
        status.value = false;
        attachmentsCount.value = 0;
        auditLogData.value = [];
      }
    }
  );
</script>

<template>
  <a-drawer
    :open="route.query.drawerFrom === 'attachmentCategories'"
    :title="
      action === 'edit'
        ? 'Update Attachment Type'
        : action === 'add' || action === 'duplicate'
          ? 'Add New Attachment Type'
          : 'Attachment Type'
    "
    width="750"
    class="monetary-drawer"
    :body-style="{ padding: '0px' }"
    :header-style="{ padding: '12px', borderBottom: '1px solid #E4E4E7' }"
    @close="props.onClose"
  >
    <template #extra>
      <div class="flex gap-3">
        <RoleBasedButton
          v-if="!props.identifier || action"
          component-name="addActionButton"
          active-class="py-2 px-3 bg-blue-600 hover:bg-blue-700 text-white rounded sm-medium duration-150 border border-blue-600"
          disabled-class="py-2 px-3 rounded sm-medium duration-150 !bg-gray-200 !shadow-[0px_1px_3px_0px_#0000001A,0px_1px_2px_0px_#0000000F] border-none"
          :loading="editStatus === 'pending'"
          @click="handleCreateAttachmentType"
        >
          {{ action === 'edit' ? 'Update Attachment Type' : 'Create Attachment Type' }}
        </RoleBasedButton>
        <RoleBasedButton
          v-if="action === 'edit' && !status"
          component-name="addActionButton"
          active-class="py-2 px-3 bg-red-600 hover:bg-red-700 text-white rounded sm-medium duration-150 border border-red-600 hover:!text-white hover:!border-red-700"
          disabled-class="py-2 px-3 rounded sm-medium duration-150 !bg-gray-200 !shadow-[0px_1px_3px_0px_#0000001A,0px_1px_2px_0px_#0000000F] border-none"
          :loading="editStatus === 'pending'"
          @click="openDisableModal"
        >
          Disable Attachment Type
        </RoleBasedButton>
        <RoleBasedButton
          v-if="action === 'edit' && status"
          component-name="addActionButton"
          active-class="py-2 px-3 bg-green-600 hover:bg-green-700 text-white rounded sm-medium duration-150 border border-green-600 hover:!text-white hover:!border-green-700"
          disabled-class="py-2 px-3 rounded sm-medium duration-150 !bg-gray-200 !shadow-[0px_1px_3px_0px_#0000001A,0px_1px_2px_0px_#0000000F] border-none"
          :loading="editStatus === 'pending'"
          @click="handleActiveAttachmentType"
        >
          Active Attachment Type
        </RoleBasedButton>
      </div>
    </template>
    <div
      v-if="action === 'edit' && attachmentsCount > 0"
      class="mx-3 mt-3 p-2 rounded-[4px] border bg-yellow-100 border-orange-400 font-semibold flex flex-col gap-2"
    >
      <div class="flex flex-row gap-2 text-wrap">
        <InformationCircleIcon class="w-5 h-5" />
        <span class="">Renaming this category won't update attachments already tagged with it.</span>
      </div>
      <div class="gap-1 text-wrap">
        <span class="text-blue-600 pr-1">{{ `${attachmentsCount} existing attachment(s)` }}</span>
        <span>currently use this category. They will keep showing the old name until re-uploaded or re-tagged.</span>
      </div>
    </div>
    <a-form
      ref="formRef"
      class="flex flex-col gap-4 p-3 setup-drawer-form"
      :model="formState"
      :rules="rules"
      :colon="false"
      :label-col="{ style: { width: '183px', textAlign: 'left' } }"
      hide-required-mark="true"
    >
      <CustomDropdown
        :options="attachmentCategoryModuleOptions"
        label-text="Module"
        name="module"
        :value="formState.module"
        state-key="module"
        :rule="rules.module"
        :is-compulsory="true"
        :disabled="action === 'edit' || !action"
        @update:value="updateStateField"
      />
      <CustomDropdown
        v-if="formState.module === 'expenses'"
        :options="expenseTypeOptions"
        label-text="Expense Type"
        name="expenseType"
        :value="formState.expenseType"
        state-key="expenseType"
        :disabled="action === 'edit' || !action"
        @update:value="updateStateField"
      />
      <CustomInput
        label-text="Category Name"
        name="categoryName"
        :value="formState.categoryName"
        state-key="categoryName"
        :is-compulsory="true"
        :is-disabled="!action"
        @update:value="updateStateField"
      />
      <CustomRadio
        :value="formState.isMandatory"
        label-text="Mandatory"
        name="isMandatory"
        state-key="isMandatory"
        :radio-options="mandatoryRadioOptions"
        :is-disabled="!action"
        @update:value="updateStateField"
      />
      <AuditLogs v-if="action !== 'add' && action !== 'duplicate'" :items="auditLogData" title="Attachment Type" />
    </a-form>
    <EditPasswordModal
      :visible="isModalVisible"
      :loading="editStatus === 'pending'"
      modal-title="Update Attachment Type"
      @update:cancel="handleModalCancel"
      @update:update="submitHandler"
    />
    <EditPasswordModal
      :visible="isModalVisibleForDisable"
      :loading="editStatus === 'pending'"
      modal-title="Disable Attachment Type"
      :warning-message="disableWarningMessage"
      @update:cancel="handleModalDisableCancel"
      @update:update="handleDisableAttachmentType"
    />
  </a-drawer>
</template>

<style>
  @import '~/assets/css/donationDrawer.css';

  .setup-drawer-form .ant-form-item {
    margin-bottom: 0px;
  }
</style>
